import { isClosedIssueStatus } from "./issue-closed-status"

/** Every kind a Suggestion row can carry. Suggestion.kind is a plain string column, so a new kind needs no migration. */
export const SUGGESTION_KINDS = [
	"link_issue",
	"link_project",
	"thread_tag",
] as const
export type SuggestionKind = (typeof SUGGESTION_KINDS)[number]

export const LINK_SUGGESTION_KINDS = [
	"link_issue",
	"link_project",
] as const satisfies readonly SuggestionKind[]

export type SuggestionSource = "model" | "rule"

export const MAX_LINK_SUGGESTIONS = 3
export const MAX_TAG_SUGGESTIONS = 2

// Past this the job has failed or is stuck, so the sidebar stops showing it as searching.
export const SUGGESTION_SEARCH_WINDOW_MS = 60_000

export interface LinkSuggestionMeta {
	linkedThreadCount: number
}

export function linkSuggestionMeta(meta: unknown): LinkSuggestionMeta {
	const count =
		meta && typeof meta === "object" && "linkedThreadCount" in meta
			? Number((meta as { linkedThreadCount: unknown }).linkedThreadCount)
			: 0
	return { linkedThreadCount: Number.isFinite(count) && count > 0 ? count : 0 }
}

/** True while a new thread's first suggestion run has not finished yet. */
export function isSearchingForSuggestions({
	createdAt,
	checkedAt,
	now,
}: {
	createdAt: number
	checkedAt: number | null | undefined
	now: number
}): boolean {
	if (checkedAt != null) return false
	const age = now - createdAt
	return age >= 0 && age < SUGGESTION_SEARCH_WINDOW_MS
}

interface SuggestedIssue {
	id: string
	isDeleted: boolean
	status: string
	statusName?: string | null
}

interface SuggestedProject {
	id: string
	isDeleted: boolean
	state?: string | null
	stateName?: string | null
}

export interface LinkSuggestionRow<
	I extends SuggestedIssue = SuggestedIssue,
	P extends SuggestedProject = SuggestedProject,
> {
	id: string
	kind: string
	targetId: string
	score: number
	dismissedAt?: number | null
	targetIssue?: I | null
	targetProject?: P | null
}

/**
 * The suggestions a teammate should still see: open, not deleted, not dismissed,
 * not already linked to the thread, best first, at most three. Runs on read, so a
 * closed or freshly linked item disappears without the job running again.
 */
export function visibleLinkSuggestions<
	I extends SuggestedIssue,
	P extends SuggestedProject,
	R extends LinkSuggestionRow<I, P>,
>(
	rows: readonly R[],
	linked: { issueIds: Iterable<string>; projectIds: Iterable<string> },
): R[] {
	const issueIds = new Set(linked.issueIds)
	const projectIds = new Set(linked.projectIds)
	return rows
		.filter((row) => {
			if (row.dismissedAt != null) return false
			if (row.kind === "link_issue") {
				const issue = row.targetIssue
				return (
					!!issue &&
					!issue.isDeleted &&
					!issueIds.has(issue.id) &&
					!isClosedIssueStatus(issue.status, issue.statusName)
				)
			}
			if (row.kind === "link_project") {
				const project = row.targetProject
				return (
					!!project &&
					!project.isDeleted &&
					!projectIds.has(project.id) &&
					!isClosedIssueStatus(project.state, project.stateName)
				)
			}
			return false
		})
		.sort((a, b) => b.score - a.score)
		.slice(0, MAX_LINK_SUGGESTIONS)
}

interface SuggestedTag {
	id: string
	isDeleted: boolean
}

export interface TagSuggestionRow<T extends SuggestedTag = SuggestedTag> {
	id: string
	kind: string
	targetId: string
	score: number
	dismissedAt?: number | null
	targetTag?: T | null
}

/** Tags still worth offering: not on the thread, not deleted, not dismissed, best first, at most two. */
export function visibleTagSuggestions<
	T extends SuggestedTag,
	R extends TagSuggestionRow<T>,
>(rows: readonly R[], appliedTagIds: Iterable<string>): R[] {
	const applied = new Set(appliedTagIds)
	return rows
		.filter(
			(row) =>
				row.kind === "thread_tag" &&
				row.dismissedAt == null &&
				!!row.targetTag &&
				!row.targetTag.isDeleted &&
				!applied.has(row.targetTag.id),
		)
		.sort((a, b) => b.score - a.score)
		.slice(0, MAX_TAG_SUGGESTIONS)
}
